import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, useAuth } from './auth/AuthContext'
import { ProtectedRoute } from './auth/ProtectedRoute'
import { SharedFileReceiver } from './components/native/SharedFileReceiver'
import { KeyboardShortcuts } from './components/layout/KeyboardShortcuts'
import { DocumentListPage } from './pages/DocumentListPage'
import { DocumentDetailPage } from './pages/DocumentDetailPage'
import { ViewerDocumentPage } from './pages/ViewerDocumentPage'
import { ViewerHomePage } from './pages/ViewerHomePage'
import { OpsPage } from './pages/OpsPage'
import { CreateDocumentPage } from './pages/CreateDocumentPage'
import { LoginPage } from './pages/LoginPage'
import { RegisterPage } from './pages/RegisterPage'
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage'
import { CreateOrganizationPage } from './pages/CreateOrganizationPage'
import { SettingsPage } from './pages/SettingsPage'
import { SharedDocumentPage } from './pages/SharedDocumentPage'
import { MeetingsPage } from './pages/MeetingsPage'
import { PersonPage } from './pages/PersonPage'
import { PeoplePage } from './pages/PeoplePage'
import { ContextManagementPage } from './pages/ContextManagementPage'
import { TasksPage } from './pages/TasksPage'
// オフライン録音ページはオフラインでも開ける必要があるため、lazyにせずメインバンドルに含める
import { RecordPage } from './pages/RecordPage'
import { OfflineRecordingSync } from './components/OfflineRecordingSync'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000, // 30秒間はキャッシュを新鮮とみなし再フェッチしない
      gcTime: 5 * 60 * 1000, // 5分間キャッシュを保持（画面遷移時に即時表示）
      refetchOnWindowFocus: false
    }
  }
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <OfflineRecordingSync />
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <SharedFileReceiver />
          <KeyboardShortcuts />
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/create-organization" element={<ProtectedRoute requireOrg={false}><CreateOrganizationPage /></ProtectedRoute>} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <MeetingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/documents"
                element={
                  <ProtectedRoute>
                    <DocumentListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/folder/:folderId"
                element={
                  <ProtectedRoute>
                    <DocumentListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create"
                element={
                  <ProtectedRoute>
                    <CreateDocumentPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/record"
                element={
                  <ProtectedRoute>
                    <RecordPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/documents/:id"
                element={
                  <ProtectedRoute allowViewer>
                    <DocumentDetailRoute />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/people"
                element={
                  <ProtectedRoute>
                    <PeoplePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/people/:id"
                element={
                  <ProtectedRoute>
                    <PersonPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tasks"
                element={
                  <ProtectedRoute>
                    <TasksPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/context"
                element={
                  <ProtectedRoute>
                    <ContextManagementPage />
                  </ProtectedRoute>
                }
              />
              {/* 旧URL互換: /meetings はホーム('/')へ */}
              <Route path="/meetings" element={<Navigate to="/" replace />} />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              {/* 旧URL互換: /organization は 設定・連携へ統合 */}
              <Route path="/organization" element={<Navigate to="/settings" replace />} />
              <Route path="/organization/*" element={<Navigate to="/settings" replace />} />
              {/* 共有リンクアクセス（認証不要） */}
              <Route
                path="/ops"
                element={
                  <ProtectedRoute>
                    <OpsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/viewer"
                element={
                  <ProtectedRoute allowViewer>
                    <ViewerHomePage />
                  </ProtectedRoute>
                }
              />
              <Route path="/share/:shareId" element={<SharedDocumentPage />} />
              
              {/* /uploadを/createにリダイレクト */}
              <Route path="/upload" element={<Navigate to="/create" replace />} />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  )
}



/** 議事録詳細: viewerロールはサマリのみの閲覧専用UIへ振り分ける */
function DocumentDetailRoute() {
  const { user } = useAuth()
  if (user?.role === 'viewer') return <ViewerDocumentPage />
  return <DocumentDetailPage />
}

export default App